import { useQuery, useQueryClient, useMutation } from "react-query";
import {
  listCitys,
  createCity,
  updateCity,
  softDeleteCity,
  softDeleteMultipleCity,
  deleteCity,
  getCityAggregate,
  getCityById,
  getCityCount,
} from "../services/City.service";

function useCityList(args) {
  const { page, limit } = args.options;
  let $and = [],
    sort = {};

  if (args.query?.$and) {
    $and = { ...args.query?.$and };
  }
  if (args.options?.sort) {
    sort = { ...args.options?.sort };
  }else{
    args.options.sort = {_id:-1}
  }
  return useQuery(["City", { page, limit, $and, sort }], () => listCitys(args));
}

function useCityCreate() {
  const queryClient = useQueryClient();
  return useMutation((record) => createCity(record), {
    onMutate: async (newRecord) => {
      await queryClient.cancelQueries(["City"]);

      const previousValue = queryClient.getQueryData(["City"]) || [];

      queryClient.setQueryData(["City"], () => [...previousValue, newRecord]);
      return previousValue;
    },
    // On failure, roll back to the previous value
    onError: (err, variables, previousValue) =>
      queryClient.setQueryData(["City"], previousValue),
    // After success or failure, refetch the Citys query
    onSettled: () => {
      queryClient.invalidateQueries(["City"]);
    },
  });
}

function useCityUpdate() {
  const queryClient = useQueryClient();
  return useMutation((record) => updateCity(record), {
    onMutate: async (updatedData) => {
      await queryClient.cancelQueries(["City"]);

      const previousValue = queryClient.getQueryData(["City"]);

      queryClient.setQueryData(["City"], (old) => {
        return old?.map((oldData) => {
          if (oldData.id === updatedData.id) return updatedData;
          else return oldData;
        });
      });
      return previousValue;
    },

    // On failure, roll back to the previous value
    onError: (err, variables, previousValue) =>
      queryClient.setQueryData(["City"], previousValue),
    // After success or failure, refetch the Citys query
    onSettled: () => {
      queryClient.invalidateQueries(["City"]);
    },
  });
}

function useCitySoftDelete() {
  const queryClient = useQueryClient();
  return useMutation((record) => softDeleteCity(record), {
    onMutate: async (deletedRecord) => {
      await queryClient.cancelQueries(["City"]);

      const previousValue = queryClient.getQueryData(["City"]) || [];
      queryClient.setQueryData(["City"], (oldData) =>
        previousValue.filter((record) => record.id !== deletedRecord.id)
      );
      return previousValue;
    },

    // On failure, roll back to the previous value
    onError: (err, variables, previousValue) =>
      queryClient.setQueryData(["City"], previousValue),
    // After success or failure, refetch the Citys query
    onSettled: () => {
      queryClient.invalidateQueries(["City"]);
    },
  });
}

function useCityMultipleSoftDelete() {
  const queryClient = useQueryClient();
  return useMutation((record) => softDeleteMultipleCity(record), {
    onMutate: async (deletedRecord) => {
      await queryClient.cancelQueries(["City"]);

      const previousValue = queryClient.getQueryData(["City"]) || [];
      queryClient.setQueryData(["City"], (oldData) =>
        previousValue.filter((record) => !deletedRecord.ids.includes(record.id))
      );
      return previousValue;
    },

    // On failure, roll back to the previous value
    onError: (err, variables, previousValue) =>
      queryClient.setQueryData(["City"], previousValue),
    // After success or failure, refetch the Citys query
    onSettled: () => {
      queryClient.invalidateQueries(["City"]);
    },
  });
}

function useCityDelete() {
  const queryClient = useQueryClient();
  return useMutation((record) => deleteCity(record), {
    onMutate: async (deletedRecord) => {
      await queryClient.cancelQueries(["City"]);

      const previousValue = queryClient.getQueryData(["City"]) || [];
      queryClient.setQueryData(["City"], (oldData) =>
        previousValue.filter((record) => record.id !== deletedRecord.id)
      );
      return previousValue;
    },

    // On failure, roll back to the previous value
    onError: (err, variables, previousValue) =>
      queryClient.setQueryData(["City"], previousValue),
    // After success or failure, refetch the Citys query
    onSettled: () => {
      queryClient.invalidateQueries(["City"]);
    },
  });
}

function useCityCount() {
  return useQuery(["CityCount"], () => {
    return getCityCount();
  });
}

function useCityAggregate(record) {
  return useQuery("City", () => {
    return getCityAggregate(record);
  });
}

function useCityGetById(id) {
  return useQuery(["City", id], () => {
    return getCityById(id);
  });
}

export {
  useCityList,
  useCityCreate,
  useCityUpdate,
  useCityDelete,
  useCityMultipleSoftDelete,
  useCityCount,
  useCitySoftDelete,
  useCityAggregate,
  useCityGetById,
};
